import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_ONE_ORDER, LIST_MENU, LIST_ONE_MENU } from "../../Graphql/queries";
import { DELETE_CART, CREATE_NEW_ORDER } from "../../Graphql/mutations";

export const useEditOrderMutations = () => {
  const [deleteCartItem] = useMutation(DELETE_CART);
  const [fetchMenu, menuQueryResult] = useLazyQuery(LIST_ONE_MENU, {
    fetchPolicy: "network-only",
  });

  return { deleteCartItem, fetchMenu, menuQueryResult };
};

// MAIN-812: adding an item used to require typing a raw Mongo menu id into a
// "Look up menu ID" box, which nobody outside engineering can know. Load the
// order's caterer menu so the admin can pick an item by name instead.
export const useCatererMenuOptions = (catererId?: string) => {
  const { data, loading } = useQuery(LIST_MENU, {
    variables: { catererId, skip: 0, limit: 500, adminFlg: true },
    skip: !catererId,
    fetchPolicy: "cache-first",
    onError: (error) => {
      console.error("GraphQL error in LIST_MENU:", error);
    },
  });

  return {
    menuOptions: data?.listMenu?.data ?? [],
    menuOptionsLoading: loading,
  };
};
import { useParams } from "react-router-dom";
import { ICartItem } from "@services/redux/order/order.typings";

export const useEditOrderState = () => {
  const { id: orderId } = useParams();

  const {
    data: orderData,
    loading: orderLoading,
    refetch: refetchOrder,
  } = useQuery(GET_ONE_ORDER, {
    variables: { orderId },
    skip: !orderId,
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("GraphQL error in GET_ONE_ORDER:", error);
    },
  });

  const [updateOrder, { loading: updateLoading }] = useMutation(CREATE_NEW_ORDER);

  const handleUpdateOrder = async (orderInput: any, cartInput: ICartItem[]) => {
    try {
      // Omit commisionAmount by destructuring, sanitize inline
      const { commisionAmount, ...sanitizedOrderInput } = orderInput;
      const response = await updateOrder({
        variables: {
          order: {
            orderId: sanitizedOrderInput?.orderId || orderId,
            customerId: sanitizedOrderInput.customerId,
            catererId: sanitizedOrderInput.catererId,
            orderStatus: sanitizedOrderInput.orderStatus || "Pending",
            orderPlaced: true,
            orderClient: sanitizedOrderInput.orderClient,
            cardId: sanitizedOrderInput.cardId,
            deliveryDate: sanitizedOrderInput.deliveryDate,
            deliveryTime: sanitizedOrderInput.deliveryTime,
            deliveryMode: sanitizedOrderInput.deliveryMode,
            deliveryFee: sanitizedOrderInput.deliveryFee,
            discountAmt: sanitizedOrderInput.discountAmt ?? 0,
            headCount: sanitizedOrderInput.headCount,
            contactPerson: sanitizedOrderInput.contactPerson,
            contactNo: sanitizedOrderInput.contactNo,
            instruction: sanitizedOrderInput.instruction,
            salesTaxPercentage: sanitizedOrderInput.salesTaxPercentage,
            locationName: sanitizedOrderInput.locationName,
            street: sanitizedOrderInput.street,
            suit: sanitizedOrderInput.suit ?? "",
            lat: sanitizedOrderInput.lat,
            lng: sanitizedOrderInput.lng,
            zip: sanitizedOrderInput.zip,
            city: sanitizedOrderInput.city,
            location: sanitizedOrderInput.location,
            locWithStreet: sanitizedOrderInput.locWithStreet || sanitizedOrderInput.street,
            state: sanitizedOrderInput.state,
            grandTotal: sanitizedOrderInput.grandTotal,
            driverTip: sanitizedOrderInput.driverTip,
            driverTipType: sanitizedOrderInput.driverTipType ?? "Amount",
            tipPercentage: sanitizedOrderInput.tipPercentage ?? 0,
            caterCash: sanitizedOrderInput.caterCash ?? 5,
            caterCashTotal: sanitizedOrderInput.caterCashTotal ?? 0,
            commision: sanitizedOrderInput.commision,
            isEditedOrder: true,
            timeZone: sanitizedOrderInput.timeZone || "America/Los_Angeles",
            dateScheduled: sanitizedOrderInput.dateScheduled || sanitizedOrderInput.deliveryDate,
            addressId: sanitizedOrderInput.addressId ?? null,
            tablewareTotal: sanitizedOrderInput.tablewareTotal ?? 0,
            tablewareDeclined: sanitizedOrderInput.tablewareDeclined ?? false,
            browserName: sanitizedOrderInput.browserName || "",
            userAgent: sanitizedOrderInput.userAgent || "",
          },
          cart: cartInput,
        },
      });
      const newOrder = response.data?.createNewOrder;
      await refetchOrder();
      return { success: true, data: newOrder };
    } catch (error) {
      console.error("Failed to update order:", error);
      return { success: false, error };
    }
  };

  // Helper function to format monetary values to two decimal places
  const formatPrice = (price: number) => price.toFixed(2);

  // Function to render item summary with add-ons details
  const renderItemSummary = (item: ICartItem) => {
    const itemTotal = item.totalPricePerUnit * item.quantity;
    const baseLine = `${item.itemName} (${item.cartType}) x${item.quantity} @ $${formatPrice(item.pricePerUnit)} each = $${itemTotal}`;

    let addOnsLines = "No add-ons";
    if (item.addOns && item.addOns.length > 0) {
      addOnsLines = "Add-ons:\n";
      item.addOns.forEach((addOn) => {
        const addOnUnit = addOn.totalPrice / addOn.addOnQuantity;
        addOnsLines += `${addOn.addOnName} (${addOn.addOnTypeName}) x${addOn.addOnQuantity} @ $${formatPrice(addOnUnit)} each = $${formatPrice(addOn.totalPrice)}\n`;
        if (addOn.subAddOns && addOn.subAddOns.length > 0) {
          addOn.subAddOns.forEach((subAddOn) => {
            const subTotal = subAddOn.subQuantity * subAddOn.subtotalPrice;
            addOnsLines += `- ${subAddOn.subAddOnName} x${subAddOn.subQuantity} - $${formatPrice(subTotal)}\n`;
          });
        }
      });
    }

    return `${baseLine}\n${addOnsLines}`;
  };

  return {
    order: orderData?.getOneOrder,
    orderLoading,
    updateLoading,
    handleUpdateOrder,
    orderId,
    refetchOrder,
    renderItemSummary,
  };
};

// Custom hook to provide menu add-on types (no auto-fetch; use explicit lazy query for menu lookup)
export const useMenuAddOns = () => {
  return {
    addOnTypes: [],
    loading: false,
  };
};